import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import RoomBasedBrowsing from '../components/RoomBasedBrowsing';
import BeforeAfterShowcase from '../components/BeforeAfterShowcase';

const Furniture = () => {
  const [filter, setFilter] = useState('all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'kitchen', name: 'Modular Kitchens' },
    { id: 'wardrobe', name: 'Wardrobes' },
    { id: 'office', name: 'Office Furniture' },
    { id: 'dining', name: 'Dining Tables' },
    { id: 'sofa', name: 'Imported Sofas' },
    { id: 'tv-unit', name: 'TV Units' },
    { id: 'residential', name: 'Custom Residential' },
    { id: 'commercial', name: 'Commercial Fitouts' }
  ];

  const products = [
    {
      id: 1,
      title: 'Premium Modular Kitchen',
      category: 'kitchen',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Contemporary design with soft-close mechanisms'
    },
    {
      id: 2,
      title: 'Executive Office Suite',
      category: 'office',
      image: 'https://images.unsplash.com/photo-1770425616829-61d6912705ad?q=80&w=2070&auto=format&fit=crop',
      description: 'Complete office furniture solutions'
    },
    {
      id: 3,
      title: 'Luxury Wardrobe Systems',
      category: 'wardrobe',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Customizable storage solutions'
    },
    {
      id: 4,
      title: 'Designer Dining Table',
      category: 'dining',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Solid wood construction with premium finish'
    },
    {
      id: 5,
      title: 'Contemporary Sofa Collection',
      category: 'sofa',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Imported premium upholstery'
    },
    {
      id: 6,
      title: 'Modern TV Unit',
      category: 'tv-unit',
      image: 'https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      description: 'Minimalist design with ample storage'
    }
  ];

  const filteredProducts = filter === 'all' 
    ? products 
    : products.filter(product => product.category === filter);

  return (
    <div className="min-h-screen pt-20" data-testid="furniture-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="furniture-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Furniture & Interiors</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Premium Furniture.<br />
              Intelligent Design.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              From modular kitchens to complete office fitouts, we deliver custom furniture solutions that 
              combine aesthetics, functionality, and durability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-200 sticky top-20 bg-white z-40" data-testid="product-filters">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                data-testid={`filter-${category.id}`}
                className={`px-6 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                  filter === category.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16" data-testid="products-grid">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`product-${product.id}`}
              >
                <div className="bg-white border border-slate-200 overflow-hidden transition-all duration-500 hover:shadow-lg">
                  <div className="relative h-80 overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-medium mb-2">{product.title}</h3>
                    <p className="text-muted-foreground text-sm">{product.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Room-Based Browsing */}
      <RoomBasedBrowsing />

      {/* Before/After Showcase */}
      <BeforeAfterShowcase />
    </div>
  );
};

export default Furniture;
